import React, { Context, createContext, ReactNode, useEffect, useState } from 'react';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import { defaults } from '../../theme/base/defaults';

export type Breakpoint = 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';

export interface Breakpoints {
  medium: boolean;
  large: boolean;
  xlarge: boolean;
  xxlarge: boolean;
  activeBreakpoint: Breakpoint;
}

const breakpointNames: Breakpoint[] = [
  'small',
  'medium',
  'large',
  'xlarge',
  'xxlarge'
];

type BreakpointContextType = Context<Breakpoints>;

const defaultContextType: Breakpoints = {
  medium: false,
  large: false,
  xlarge: false,
  xxlarge: false,
  activeBreakpoint: 'small'
};

export const BreakpointContext: BreakpointContextType = createContext(
  defaultContextType
);

interface BreakpointProviderProps {
  children: ReactNode;
}

function BreakpointProvider(props: BreakpointProviderProps) {
  const { children } = props;

  const [breakpoints, setBreakpoints] = useState<Breakpoints | void>();

  useEffect(() => {
    function onResize() {
      const { innerWidth } = window;
      const medium = innerWidth >= defaults.breakpoints.medium;
      const large = innerWidth >= defaults.breakpoints.large;
      const xlarge = innerWidth >= defaults.breakpoints.xlarge;
      const xxlarge = innerWidth >= defaults.breakpoints.xxlarge;
      const activeBreakpoint =
        breakpointNames[[medium, large, xlarge, xxlarge].lastIndexOf(true) + 1];
      if (
        !breakpoints ||
        medium !== breakpoints.medium ||
        large !== breakpoints.large ||
        xlarge !== breakpoints.xlarge ||
        xxlarge !== breakpoints.xxlarge
      ) {
        setBreakpoints({
          medium,
          large,
          xlarge,
          xxlarge,
          activeBreakpoint
        });
      }
    }

    window.addEventListener('resize', onResize);

    // Also check breakpoints immediately to set them initially and also avoid print bug in FF #58809
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [breakpoints]);

  if (!breakpoints) {
    return null;
  }

  return (
    <BreakpointContext.Provider value={breakpoints}>
      {children}
    </BreakpointContext.Provider>
  );
}

export default withErrorBoundary(BreakpointProvider, 'BreakpointProvider');
